import {
  currencyDigits,
  fromQty,
  parseDecimal,
  parseQty,
  roundUpToUnit,
  toMinor,
  UNITS,
  type UnitCode,
  unitStep,
} from "@bt/domain";
import { useMutation } from "@tanstack/react-query";
import { MinusIcon, PlusIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { inputClass } from "@/components/ui/input";
import { useFormat } from "@/lib/format";
import { usePriceBasis } from "@/lib/prices";
import { useTRPC } from "@/lib/trpc";
import { cn } from "@/lib/utils";

/**
 * Number field for a quantity in milli-units (architecture.md › Quantities and money). Accepts
 * "5,5" and "5.5", shows the unit, and has −/+ buttons stepping by the unit's step. Amounts finer
 * than the unit allows round up (6,5 pieces become 7), which the field shows once it loses focus.
 */
export function QuantityInput({
  value,
  onChange,
  unit,
  id,
  min = 0,
  allowEmpty = false,
  steppers = true,
  className,
  autoFocus,
  invalid,
  "aria-label": ariaLabel,
}: {
  value: number | null;
  onChange: (value: number | null) => void;
  unit: UnitCode;
  id?: string;
  min?: number;
  allowEmpty?: boolean;
  steppers?: boolean;
  className?: string;
  autoFocus?: boolean;
  invalid?: boolean;
  "aria-label"?: string;
}) {
  const { t } = useTranslation();
  const format = useFormat();
  const toText = (v: number | null) =>
    v === null ? "" : format.inputNumber(fromQty(v), UNITS[unit].decimals);
  const [text, setText] = useState(() => toText(value));
  const lastValue = useRef(value);

  useEffect(() => {
    if (value !== lastValue.current) {
      lastValue.current = value;
      setText(toText(value));
    }
  });

  const commit = (next: number | null) => {
    lastValue.current = next;
    onChange(next);
  };

  const step = (direction: 1 | -1) => {
    const current = value ?? 0;
    const next = Math.max(min, current + direction * unitStep(unit));
    setText(toText(next));
    commit(next);
  };

  return (
    <div
      className={cn(
        inputClass,
        "flex items-center gap-1 px-1 focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/25",
        invalid && "border-destructive",
        className,
      )}
    >
      {steppers ? (
        <button
          type="button"
          tabIndex={-1}
          aria-label={t("parts.decrease")}
          onClick={() => step(-1)}
          disabled={(value ?? 0) <= min}
          className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40"
        >
          <MinusIcon className="size-3.5" />
        </button>
      ) : null}
      <input
        id={id}
        aria-label={ariaLabel}
        inputMode="decimal"
        autoComplete="off"
        // biome-ignore lint/a11y/noAutofocus: callers opt in for dialogs that exist to edit this one value
        autoFocus={autoFocus}
        aria-invalid={invalid || undefined}
        value={text}
        onChange={(event) => {
          setText(event.target.value);
          if (event.target.value.trim() === "") {
            if (allowEmpty) commit(null);
            return;
          }
          const parsed = parseQty(event.target.value);
          if (parsed !== null) commit(Math.max(min, roundUpToUnit(parsed, unit)));
        }}
        onBlur={() => setText(toText(value))}
        onKeyDown={(event) => {
          if (event.key === "ArrowUp") {
            event.preventDefault();
            step(1);
          } else if (event.key === "ArrowDown") {
            event.preventDefault();
            step(-1);
          }
        }}
        className="h-full w-full min-w-0 bg-transparent text-center tabular outline-none"
      />
      <span className="shrink-0 pr-1 text-xs text-muted-foreground">{t(`unitShort.${unit}`)}</span>
      {steppers ? (
        <button
          type="button"
          tabIndex={-1}
          aria-label={t("parts.increase")}
          onClick={() => step(1)}
          className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <PlusIcon className="size-3.5" />
        </button>
      ) : null}
    </div>
  );
}

/**
 * Price field in minor units, e.g. "12,99" → 1299, on the garage's price basis: the server stores
 * it without tax (ADR 0009). The label names the tax rate: today's, or an order's own. Narrow
 * fields (`showBasis={false}`) show only the currency; their screen names the basis elsewhere.
 */
export function MoneyInput({
  value,
  onChange,
  currency,
  taxRateBp = "today",
  showBasis = true,
  id,
  className,
  invalid,
  "aria-label": ariaLabel,
}: {
  value: number | null;
  onChange: (value: number | null) => void;
  currency: string;
  taxRateBp?: number | "today";
  showBasis?: boolean;
  id?: string;
  className?: string;
  invalid?: boolean;
  "aria-label"?: string;
}) {
  const format = useFormat();
  const priceBasis = usePriceBasis(taxRateBp);
  const digits = currencyDigits(currency);
  const toText = (v: number | null) =>
    v === null ? "" : format.inputNumber(v / 10 ** digits, digits, digits);
  const [text, setText] = useState(() => toText(value));
  const lastValue = useRef(value);
  useEffect(() => {
    if (value !== lastValue.current) {
      lastValue.current = value;
      setText(toText(value));
    }
  });
  return (
    <div
      className={cn(
        inputClass,
        "flex items-center gap-2 focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/25",
        invalid && "border-destructive",
        className,
      )}
    >
      <input
        id={id}
        aria-label={ariaLabel}
        inputMode="decimal"
        autoComplete="off"
        value={text}
        aria-invalid={invalid || undefined}
        onChange={(event) => {
          setText(event.target.value);
          if (event.target.value.trim() === "") {
            lastValue.current = null;
            onChange(null);
            return;
          }
          const amount = parseDecimal(event.target.value);
          if (amount !== null && amount >= 0) {
            const minor = toMinor(amount, currency);
            lastValue.current = minor;
            onChange(minor);
          }
        }}
        onBlur={() => setText(toText(value))}
        className="h-full w-full min-w-0 bg-transparent tabular outline-none"
      />
      <span className="shrink-0 text-xs whitespace-nowrap text-muted-foreground">
        {showBasis ? `${currency} · ${priceBasis}` : currency}
      </span>
    </div>
  );
}

/**
 * Inline −/+ for on hand. Rapid clicks are batched into one stock movement; the toast offers undo.
 */
export function StockStepper({
  partId,
  partName,
  onHand,
  unit,
  size = "default",
}: {
  partId: string;
  partName: string;
  onHand: number;
  unit: UnitCode;
  size?: "default" | "lg";
}) {
  const { t } = useTranslation();
  const format = useFormat();
  const trpc = useTRPC();
  // Clicks not sent yet, and amounts sent but not yet reflected in `onHand`.
  const [pending, setPending] = useState(0);
  const [inflight, setInflight] = useState(0);
  const pendingRef = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: a fresh on-hand value settles what was sent
  useEffect(() => setInflight(0), [onHand]);

  const undo = useMutation(trpc.parts.undoMovement.mutationOptions());
  const adjust = useMutation({
    ...trpc.parts.adjust.mutationOptions(),
    onSuccess: (result, variables) => {
      toast(t("parts.adjusted", { name: partName, delta: format.delta(variables.delta, unit) }), {
        action: {
          label: t("common.undo"),
          onClick: () =>
            undo.mutate(
              { movementId: result.movementId },
              { onSuccess: () => toast.success(t("parts.undone")) },
            ),
        },
      });
    },
    onError: () => setInflight(0),
  });

  const flush = () => {
    const delta = pendingRef.current;
    pendingRef.current = 0;
    timer.current = null;
    setPending(0);
    if (delta === 0) return;
    setInflight((value) => value + delta);
    adjust.mutate({ partId, delta, note: null });
  };

  const bump = (direction: 1 | -1) => {
    pendingRef.current += unitStep(unit) * direction;
    setPending(pendingRef.current);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(flush, 650);
  };

  // On unmount, send whatever is still pending.
  const flushRef = useRef(flush);
  flushRef.current = flush;
  useEffect(
    () => () => {
      if (timer.current) {
        clearTimeout(timer.current);
        flushRef.current();
      }
    },
    [],
  );

  const shown = onHand + inflight + pending;
  const large = size === "lg";
  return (
    <div
      className={cn(
        "inline-flex items-center rounded-lg border border-border bg-surface shadow-xs",
        large ? "h-11" : "h-8",
      )}
    >
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          bump(-1);
        }}
        disabled={shown <= 0}
        aria-label={`${t("parts.decrease")}: ${partName}`}
        className={cn(
          "flex h-full items-center justify-center rounded-l-lg text-muted-foreground transition hover:bg-muted hover:text-foreground disabled:opacity-35",
          large ? "w-11" : "w-8",
        )}
      >
        <MinusIcon className={large ? "size-4" : "size-3.5"} />
      </button>
      <span
        className={cn(
          "min-w-12 border-x border-border px-2 text-center font-medium tabular",
          large ? "text-lg leading-[2.6rem]" : "text-[13px] leading-[1.9rem]",
          pending + inflight !== 0 && "text-brand",
          shown < 0 && "text-destructive",
        )}
        aria-live="polite"
      >
        {format.qtyNumber(shown, unit)}
      </span>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          bump(1);
        }}
        aria-label={`${t("parts.increase")}: ${partName}`}
        className={cn(
          "flex h-full items-center justify-center rounded-r-lg text-muted-foreground transition hover:bg-muted hover:text-foreground",
          large ? "w-11" : "w-8",
        )}
      >
        <PlusIcon className={large ? "size-4" : "size-3.5"} />
      </button>
    </div>
  );
}
